/**
 * V2 — Buckets de disponibilité pour le Dashboard CLevel.
 *
 * Le user a retiré la distinction "EN PRÉPARATION" vs "DISPONIBLE" dans la
 * fiche bien. La V2 segmente désormais la vacance par ANCIENNETÉ de mise sur
 * le marché (`seraDispoPour`) — 5 buckets MUTUELLEMENT EXCLUSIFS pour
 * piloter la vacance locative.
 *
 * Critère d'éligibilité aux buckets :
 *  - bien actif (pas LOUÉ, pas VENDU)
 *  - isCLevelReady (les 11 champs requis sont remplis)
 *  - getMailingCompleteness.complete === true (les 15 champs mailing + cond.)
 *
 * Sinon → exclus des buckets (apparaissent dans "Incomplets mailing").
 */

import type { BienRow } from "@/schemas/bien";
import { ACTIVE_MAILING_FIELDS, getConditionalMailingFields, type BienDraft } from "@/lib/bienPrepValidation";
import { isCLevelReady } from "./bienLifecycle";
import { today } from "@/lib/today";
import { isPebExpiringSoon } from "@/lib/pebCycle";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MarketBucket = "future" | "lt-1m" | "1-3m" | "3-6m" | "gt-6m";

// labelKey / hintKey = clés i18n (namespace common). Résolues via t() côté React.
export const MARKET_BUCKETS: { key: MarketBucket; labelKey: string; hintKey: string }[] = [
  { key: "future", labelKey: "v2.buckets.future.label", hintKey: "v2.buckets.future.hint" },
  { key: "lt-1m",  labelKey: "v2.buckets.lt-1m.label",  hintKey: "v2.buckets.lt-1m.hint" },
  { key: "1-3m",   labelKey: "v2.buckets.1-3m.label",   hintKey: "v2.buckets.1-3m.hint" },
  { key: "3-6m",   labelKey: "v2.buckets.3-6m.label",   hintKey: "v2.buckets.3-6m.hint" },
  { key: "gt-6m",  labelKey: "v2.buckets.gt-6m.label",  hintKey: "v2.buckets.gt-6m.hint" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  return Boolean(value);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Complétude mailing : retourne `complete: true` si tous les champs
 * ACTIVE_MAILING_FIELDS + conditionnels (caveNumero, parkingNumero,
 * parkingLoyerMensuel selon les valeurs courantes) sont remplis.
 *
 * Pas de check de statut — c'est le caller qui décide quels biens valider
 * (ex. Dashboard ne valide que les non-loués/non-vendus).
 */
export function getMailingCompleteness(bien: BienRow): { complete: boolean; missing: string[] } {
  const draft = bien as BienDraft;
  const fields = [
    ...ACTIVE_MAILING_FIELDS,
    ...getConditionalMailingFields(draft),
  ];
  const missing = fields.filter((k) => !isFilled(draft[k]));
  return { complete: missing.length === 0, missing };
}

/**
 * Statut du bien dans le marché. Retourne null si le bien n'est pas
 * éligible (loué, vendu, CLevel incomplet, mailing incomplet, ou pas de
 * date `seraDispoPour`).
 */
export function getMarketBucket(bien: BienRow): MarketBucket | null {
  // Loué / vendu : pas dans les buckets de vacance
  const dispo = (bien.disponibilite || "").toUpperCase();
  if (dispo === "LOUÉ" || dispo === "VENDU") return null;

  // CLevel pas prêt ou mailing incomplet : pas dans les buckets
  if (!isCLevelReady(bien).ok) return null;
  if (!getMailingCompleteness(bien).complete) return null;

  // Pas de date dispo : exclu (le bien n'a pas de point de référence temporel)
  if (!bien.seraDispoPour) return null;
  const d = new Date(bien.seraDispoPour);
  if (isNaN(d.getTime())) return null;

  const now = today();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / MS_PER_DAY);

  if (diffDays < 0) return "future";
  if (diffDays < 30) return "lt-1m";
  if (diffDays < 90) return "1-3m";
  if (diffDays < 180) return "3-6m";
  return "gt-6m";
}

/**
 * Compte tous les biens dans chaque bucket. Pratique pour le Dashboard.
 */
export function countByBucket(biens: BienRow[]): Record<MarketBucket, number> {
  const out: Record<MarketBucket, number> = {
    future: 0,
    "lt-1m": 0,
    "1-3m": 0,
    "3-6m": 0,
    "gt-6m": 0,
  };
  for (const b of biens) {
    const bucket = getMarketBucket(b);
    if (bucket) out[bucket]++;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Qualité PEB — KPI dashboard + filtres catalogue (source UNIQUE, anti-drift :
// le compteur de la tuile et la liste filtrée du catalogue utilisent ces mêmes
// prédicats).
// ---------------------------------------------------------------------------

/** PEB absent : aucune classe PEB renseignée, sur un bien non vendu. */
export function isPebAbsent(b: BienRow): boolean {
  if ((b.disponibilite || "").toUpperCase() === "VENDU") return false;
  return !b.classePeb;
}

/**
 * PEB présent (classe renseignée) mais à renouveler, sur un bien non vendu :
 * date d'expiration manquante/invalide, déjà expirée, ou échéance ≤ 6 mois.
 * Délègue la règle au source unique (@/lib/pebCycle). Le cas "classe absente"
 * relève de isPebAbsent (tuile séparée) → on garde `classePeb` pour ne pas
 * doubler le comptage entre les deux tuiles.
 */
export function isPebExpiring6m(b: BienRow): boolean {
  if ((b.disponibilite || "").toUpperCase() === "VENDU") return false;
  return !!b.classePeb && isPebExpiringSoon(b.pebFinLe, b.classePeb);
}
